import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  LinearProgress
} from '@mui/material';
import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import {
  KOTTAYIL_DISTANCE_REFERENCE,
  KOTTAYIL_NEARBY_TEMPLE_DEFAULTS,
  mergeWithKottayilTempleDefaults,
} from '../../data/nearbyTemplesDefaults';

const GOLD = '#d4af37';
const TEMPLE_IMAGE_DOC_REF_PREFIX = 'temple-image-doc:';

interface TempleItem {
  id: string;
  name: string;
  distance: string;
  deity: string;
  location: string;
  description: string;
  color?: string;
  imageUrl?: string;
  mapUrl?: string;
  lat?: number;
  lng?: number;
}

interface TemplesContent {
  title: string;
  description: string;
  temples: TempleItem[];
}

interface TempleItemLike {
  id?: string;
  name?: string;
  distance?: string;
  deity?: string;
  location?: string;
  description?: string;
  color?: string;
  imageUrl?: string;
  mapUrl?: string;
  lat?: number | string;
  lng?: number | string;
}

const isBase64TempleImage = (imageUrl?: string) =>
  typeof imageUrl === 'string' && imageUrl.startsWith('data:image/');

const getTempleSlug = (temple?: TempleItem) =>
  (temple?.name || 'temple')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const isTempleImageDocRef = (value?: string) =>
  typeof value === 'string' && value.startsWith(TEMPLE_IMAGE_DOC_REF_PREFIX);

const buildTempleImageDocRef = (docId: string) =>
  `${TEMPLE_IMAGE_DOC_REF_PREFIX}${docId}`;

const extractTempleImageDocId = (value?: string) => {
  if (!isTempleImageDocRef(value)) return '';
  return value.slice(TEMPLE_IMAGE_DOC_REF_PREFIX.length).trim();
};

const getTempleImageDocId = (temple: TempleItem, index: number) => {
  const raw = (temple.id || getTempleSlug(temple) || `temple_${index + 1}`)
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 100);
  return `temple_image_${raw || `idx_${index + 1}`}`;
};

const toOptionalNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const normalizeTemples = (temples: TempleItemLike[] = []): TempleItem[] => {
  const seen = new Set<string>();

  return temples.map((temple, index) => {
    const baseId = (temple.id || '').trim() || `temple-${index + 1}`;
    let uniqueId = baseId;
    let suffix = 2;
    while (seen.has(uniqueId)) {
      uniqueId = `${baseId}-${suffix}`;
      suffix += 1;
    }
    seen.add(uniqueId);

    return {
      id: uniqueId,
      name: temple.name || '',
      distance: temple.distance || '',
      deity: temple.deity || '',
      location: temple.location || '',
      description: temple.description || '',
      color: temple.color || '#9c27b0',
      imageUrl: temple.imageUrl || '',
      mapUrl: temple.mapUrl || '',
      lat: toOptionalNumber(temple.lat),
      lng: toOptionalNumber(temple.lng),
    };
  });
};

const compressImageToBase64 = (
  file: File,
  maxWidth = 960,
  maxHeight = 720,
  quality = 0.72,
  maxBase64Length = 110_000
): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to initialize image canvas'));
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      let q = quality;
      let base64 = canvas.toDataURL('image/jpeg', q);
      while (base64.length > maxBase64Length && q > 0.45) {
        q -= 0.05;
        base64 = canvas.toDataURL('image/jpeg', q);
      }

      let workingCanvas = canvas;
      while (base64.length > maxBase64Length && workingCanvas.width > 320 && workingCanvas.height > 240) {
        const smallCanvas = document.createElement('canvas');
        const scale = 0.82;
        smallCanvas.width = Math.round(workingCanvas.width * scale);
        smallCanvas.height = Math.round(workingCanvas.height * scale);
        const sCtx = smallCanvas.getContext('2d');
        if (!sCtx) {
          reject(new Error('Failed to initialize compression canvas'));
          return;
        }
        sCtx.imageSmoothingEnabled = true;
        sCtx.imageSmoothingQuality = 'high';
        sCtx.drawImage(workingCanvas, 0, 0, smallCanvas.width, smallCanvas.height);
        q = Math.max(0.45, q - 0.06);
        base64 = smallCanvas.toDataURL('image/jpeg', q);
        workingCanvas = smallCanvas;
      }

      resolve(base64);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });

const recompressDataUrlToBase64 = (
  dataUrl: string,
  maxWidth = 900,
  maxHeight = 680,
  quality = 0.66,
  maxBase64Length = 75_000
): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to initialize recompression canvas'));
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      let q = quality;
      let base64 = canvas.toDataURL('image/jpeg', q);
      while (base64.length > maxBase64Length && q > 0.4) {
        q -= 0.05;
        base64 = canvas.toDataURL('image/jpeg', q);
      }

      let workingCanvas = canvas;
      while (base64.length > maxBase64Length && workingCanvas.width > 280 && workingCanvas.height > 210) {
        const smallCanvas = document.createElement('canvas');
        const scale = 0.8;
        smallCanvas.width = Math.round(workingCanvas.width * scale);
        smallCanvas.height = Math.round(workingCanvas.height * scale);
        const sCtx = smallCanvas.getContext('2d');
        if (!sCtx) {
          reject(new Error('Failed to initialize progressive recompression canvas'));
          return;
        }
        sCtx.imageSmoothingEnabled = true;
        sCtx.imageSmoothingQuality = 'high';
        sCtx.drawImage(workingCanvas, 0, 0, smallCanvas.width, smallCanvas.height);
        q = Math.max(0.38, q - 0.06);
        base64 = smallCanvas.toDataURL('image/jpeg', q);
        workingCanvas = smallCanvas;
      }

      resolve(base64);
    };

    img.onerror = () => reject(new Error('Failed to load base64 image for recompression'));
    img.src = dataUrl;
  });

const getApproxDocSizeBytes = (value: unknown) => {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    return Number.MAX_SAFE_INTEGER;
  }
};

const optimizeTemplesForFirestoreSize = async (input: TemplesContent) => {
  const maxBytes = 950_000;
  const targetBytes = 860_000;
  const optimized: TemplesContent = {
    ...input,
    temples: input.temples.map((temple) => ({ ...temple })),
  };

  let sizeBytes = getApproxDocSizeBytes(optimized);
  if (sizeBytes <= maxBytes) {
    return { content: optimized, sizeBytes, optimizedCount: 0 };
  }

  let optimizedCount = 0;
  const sortedIndices = optimized.temples
    .map((temple, index) => ({
      index,
      len: isBase64TempleImage(temple.imageUrl) ? (temple.imageUrl || '').length : 0,
    }))
    .filter((item) => item.len > 0)
    .sort((a, b) => b.len - a.len)
    .map((item) => item.index);

  for (const index of sortedIndices) {
    const current = optimized.temples[index].imageUrl || '';
    try {
      const compact = await recompressDataUrlToBase64(current, 900, 680, 0.66, 75_000);
      if (compact.length < current.length) {
        optimized.temples[index] = { ...optimized.temples[index], imageUrl: compact };
        optimizedCount += 1;
        sizeBytes = getApproxDocSizeBytes(optimized);
        if (sizeBytes <= targetBytes) {
          return { content: optimized, sizeBytes, optimizedCount };
        }
      }
    } catch (error) {
      console.error('Failed to recompress base64 temple image (pass 1):', error);
    }
  }

  if (sizeBytes > maxBytes) {
    for (const index of sortedIndices) {
      const current = optimized.temples[index].imageUrl || '';
      try {
        const compact = await recompressDataUrlToBase64(current, 760, 560, 0.58, 50_000);
        if (compact.length < current.length) {
          optimized.temples[index] = { ...optimized.temples[index], imageUrl: compact };
          optimizedCount += 1;
          sizeBytes = getApproxDocSizeBytes(optimized);
          if (sizeBytes <= targetBytes) {
            break;
          }
        }
      } catch (error) {
        console.error('Failed to recompress base64 temple image (pass 2):', error);
      }
    }
  }

  sizeBytes = getApproxDocSizeBytes(optimized);
  return { content: optimized, sizeBytes, optimizedCount };
};

const getErrorDetails = (error: unknown) => {
  const errorCode = typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code?: unknown }).code ?? '')
    : '';
  const errorMessage = typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message?: unknown }).message ?? '')
    : 'Unknown upload error';
  return { errorCode, errorMessage };
};

const toUserFriendlyUploadMessage = (error: unknown) => {
  const { errorCode, errorMessage } = getErrorDetails(error);
  if (errorCode === 'storage/unauthorized') {
    return 'Storage permission denied for this account';
  }
  if (errorCode === 'storage/no-default-bucket') {
    return 'Storage bucket is not configured';
  }
  if (errorCode === 'storage/canceled') {
    return 'Upload was canceled';
  }
  if (errorCode === 'storage/unknown') {
    return 'Storage returned an unknown error';
  }
  return errorCode ? `${errorCode}: ${errorMessage}` : errorMessage;
};

const TemplesNearbyManager = () => {
  const [content, setContent] = useState<TemplesContent>({
    title: 'Temples Nearby',
    description: `Curated temple circuit around Muchukunnu. ${KOTTAYIL_DISTANCE_REFERENCE}.`,
    temples: KOTTAYIL_NEARBY_TEMPLE_DEFAULTS,
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [uploadingTempleId, setUploadingTempleId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const templeFileInputRef = useRef<HTMLInputElement>(null);
  const pendingTempleId = useRef<string | null>(null);
  const templeFocusHandler = useRef<(() => void) | null>(null);

  const hydrateTempleImageRefs = useCallback(async (temples: TempleItem[]) => {
    const hydrated = await Promise.all(
      temples.map(async (temple) => {
        const imageDocId = extractTempleImageDocId(temple.imageUrl);
        if (!imageDocId) return temple;
        try {
          const imageSnap = await getDoc(doc(db, 'nearby_content', imageDocId));
          if (!imageSnap.exists()) {
            return { ...temple, imageUrl: '' };
          }
          const imageData = imageSnap.data()?.imageData;
          if (typeof imageData === 'string' && imageData.length > 0) {
            return { ...temple, imageUrl: imageData };
          }
          return { ...temple, imageUrl: '' };
        } catch (error) {
          console.error(`Failed to load temple image doc: ${imageDocId}`, error);
          return { ...temple, imageUrl: '' };
        }
      })
    );

    return hydrated;
  }, []);

  const persistTemplesContent = useCallback(async (nextContent: TemplesContent) => {
    const transformedTemples: TempleItem[] = [];
    let externalDocWriteFailed = false;

    for (let index = 0; index < nextContent.temples.length; index += 1) {
      const temple = nextContent.temples[index];
      const currentImage = temple.imageUrl || '';
      if (!isBase64TempleImage(currentImage)) {
        transformedTemples.push(temple);
        continue;
      }

      const imageDocId = getTempleImageDocId(temple, index);
      try {
        await setDoc(doc(db, 'nearby_content', imageDocId), {
          type: 'temple_image',
          templeId: temple.id || '',
          templeName: temple.name || '',
          imageData: currentImage,
          updatedAt: Date.now(),
        });
        transformedTemples.push({ ...temple, imageUrl: buildTempleImageDocRef(imageDocId) });
      } catch (error) {
        externalDocWriteFailed = true;
        console.error(`Failed to write temple image doc: ${imageDocId}`, error);
        transformedTemples.push(temple);
      }
    }

    const payload: TemplesContent = {
      ...nextContent,
      temples: transformedTemples,
    };

    if (!externalDocWriteFailed) {
      await setDoc(doc(db, 'nearby_content', 'temples'), payload);
      return payload;
    }

    const optimized = await optimizeTemplesForFirestoreSize(payload);
    if (optimized.sizeBytes > 990_000) {
      throw new Error('Image save failed. Firestore prevented image-doc writes and the temples document is still too large.');
    }
    await setDoc(doc(db, 'nearby_content', 'temples'), optimized.content);
    return optimized.content;
  }, []);

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'nearby_content', 'temples'), (docSnap) => {
        void (async () => {
          if (docSnap.exists()) {
            const data = docSnap.data() as TemplesContent;
            const mergedTemples = mergeWithKottayilTempleDefaults(
              Array.isArray(data.temples) ? (data.temples as TempleItemLike[]) : []
            );
            const currentTemples = normalizeTemples(mergedTemples);
            const hydratedTemples = await hydrateTempleImageRefs(currentTemples);

            setContent({
              title: data.title || 'Temples Nearby',
              description: data.description || `Curated temple circuit around Muchukunnu. ${KOTTAYIL_DISTANCE_REFERENCE}.`,
              temples: hydratedTemples,
            });
          } else {
            setContent({
              title: 'Temples Nearby',
              description: `Curated temple circuit around Muchukunnu. ${KOTTAYIL_DISTANCE_REFERENCE}.`,
              temples: normalizeTemples(KOTTAYIL_NEARBY_TEMPLE_DEFAULTS),
            });
          }
          setLoading(false);
        })();
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, [hydrateTempleImageRefs]);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistTemplesContent(content);
      showSnackbar('Temples data saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar(`Failed to save content: ${toUserFriendlyUploadMessage(error)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddTemple = () => {
    const newTemple: TempleItem = {
      id: Date.now().toString(),
      name: 'New Temple',
      distance: '',
      deity: '',
      location: '',
      description: '',
      color: '#9c27b0',
      imageUrl: '',
      mapUrl: '',
      lat: undefined,
      lng: undefined,
    };
    setContent({
      ...content,
      temples: [...content.temples, newTemple]
    });
  };

  const handleTempleChange = (id: string, field: keyof TempleItem, value: string) => {
    setContent({
      ...content,
      temples: content.temples.map(temple =>
        temple.id === id ? { ...temple, [field]: value } : temple
      )
    });
  };

  const handleTempleCoordinateChange = (id: string, field: 'lat' | 'lng', rawValue: string) => {
    const trimmed = rawValue.trim();
    const numericValue = trimmed.length === 0 ? undefined : Number(trimmed);
    const safeValue = typeof numericValue === 'number' && Number.isFinite(numericValue) ? numericValue : undefined;
    setContent({
      ...content,
      temples: content.temples.map((temple) =>
        temple.id === id ? { ...temple, [field]: safeValue } : temple
      ),
    });
  };

  const handleDeleteTemple = (id: string) => {
    setContent({
      ...content,
      temples: content.temples.filter(temple => temple.id !== id)
    });
  };

  const handleTempleFileUpload = async (id: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      showSnackbar('Please select an image smaller than 8MB', 'error');
      return;
    }
    setUploadingTempleId(id);
    setUploadProgress(10);

    try {
      setUploadProgress(30);
      const base64 = await compressImageToBase64(file);
      setUploadProgress(90);

      setContent((prev) => ({
        ...prev,
        temples: prev.temples.map((item) =>
          item.id === id ? { ...item, imageUrl: base64 } : item
        ),
      }));

      setUploadProgress(100);
      const originalKB = Math.round(file.size / 1024);
      const compressedKB = Math.round((base64.length * 3) / 4 / 1024);
      setTimeout(() => {
        setUploadingTempleId(null);
        setUploadProgress(0);
        showSnackbar(
          `Image compressed (${originalKB}KB → ${compressedKB}KB). Click "Save Changes" to persist.`,
          'success'
        );
      }, 400);
    } catch (error) {
      console.error('Temple image upload failed:', error);
      showSnackbar('Failed to process image', 'error');
      setUploadingTempleId(null);
      setUploadProgress(0);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Temples Nearby Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage nearby temples content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🏛️ Section Settings
        </Typography>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Distances should be entered as distance from <strong>Kottayil Shiva Temple</strong> (for example, <code>4.6 km</code>).
          Add map URL (or latitude/longitude) for exact navigation, and add image URL or upload an image.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Title"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Description"
              value={content.description}
              onChange={(e) => setContent({ ...content, description: e.target.value })}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            ⛩️ Temples ({content.temples.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddTemple}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Temple
          </Button>
        </Box>

        <Grid container spacing={2}>
          {content.temples.map((temple) => (
            <Grid item xs={12} key={temple.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Temple Name"
                      value={temple.name}
                      onChange={(e) => handleTempleChange(temple.id, 'name', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Deity"
                      value={temple.deity}
                      onChange={(e) => handleTempleChange(temple.id, 'deity', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label="Distance from Kottayil Shiva Temple"
                      value={temple.distance}
                      onChange={(e) => handleTempleChange(temple.id, 'distance', e.target.value)}
                      size="small"
                      placeholder="e.g., 4.6 km"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => handleDeleteTemple(temple.id)}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={temple.location}
                      onChange={(e) => handleTempleChange(temple.id, 'location', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Color (Hex)"
                      value={temple.color || '#9c27b0'}
                      onChange={(e) => handleTempleChange(temple.id, 'color', e.target.value)}
                      size="small"
                      placeholder="#9c27b0"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Map URL (Google Maps)"
                      value={temple.mapUrl || ''}
                      onChange={(e) => handleTempleChange(temple.id, 'mapUrl', e.target.value)}
                      size="small"
                      placeholder="https://www.google.com/maps/search/?api=1&query=..."
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      label="Latitude"
                      value={temple.lat ?? ''}
                      onChange={(e) => handleTempleCoordinateChange(temple.id, 'lat', e.target.value)}
                      size="small"
                      placeholder="11.4806"
                      type="number"
                      inputProps={{ step: 'any' }}
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      label="Longitude"
                      value={temple.lng ?? ''}
                      onChange={(e) => handleTempleCoordinateChange(temple.id, 'lng', e.target.value)}
                      size="small"
                      placeholder="75.5916"
                      type="number"
                      inputProps={{ step: 'any' }}
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={temple.description}
                      onChange={(e) => handleTempleChange(temple.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={isBase64TempleImage(temple.imageUrl) ? '(Uploaded image)' : temple.imageUrl || ''}
                      onChange={(e) => handleTempleChange(temple.id, 'imageUrl', e.target.value)}
                      size="small"
                      disabled={isBase64TempleImage(temple.imageUrl)}
                      placeholder="https://example.com/temple.jpg"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploadingTempleId === temple.id}
                        onClick={() => {
                          pendingTempleId.current = temple.id;
                          templeFileInputRef.current?.click();
                          const onFocus = () => {
                            setTimeout(() => {
                              if (pendingTempleId.current === temple.id) {
                                pendingTempleId.current = null;
                              }
                              setUploadingTempleId((prev) => prev === temple.id ? null : prev);
                              setUploadProgress(0);
                            }, 300);
                            window.removeEventListener('focus', onFocus);
                            templeFocusHandler.current = null;
                          };
                          templeFocusHandler.current = onFocus;
                          window.addEventListener('focus', onFocus);
                        }}
                        sx={{
                          borderColor: GOLD,
                          color: GOLD,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          textTransform: 'none',
                          height: 34,
                          '&:hover': { bgcolor: `${GOLD}15`, borderColor: GOLD },
                        }}
                      >
                        {uploadingTempleId === temple.id ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      {temple.imageUrl && !isBase64TempleImage(temple.imageUrl) && (
                        <Button
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleTempleChange(temple.id, 'imageUrl', '')}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#888' }}
                        >
                          Clear Image
                        </Button>
                      )}
                      {isBase64TempleImage(temple.imageUrl) && (
                        <Button
                          size="small"
                          startIcon={<LinkIcon />}
                          onClick={() => handleTempleChange(temple.id, 'imageUrl', '')}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#888' }}
                        >
                          Clear & use URL
                        </Button>
                      )}
                    </Box>
                    {uploadingTempleId === temple.id && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ mt: 0.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: GOLD } }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {temple.imageUrl && (
                      <Box sx={{ width: '100%', height: 100, borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
                        <img src={temple.imageUrl} alt={temple.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <input
        type="file"
        accept="image/*"
        ref={templeFileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (templeFocusHandler.current) {
            window.removeEventListener('focus', templeFocusHandler.current);
            templeFocusHandler.current = null;
          }
          const file = e.target.files?.[0];
          const targetTempleId = pendingTempleId.current;
          if (file && targetTempleId) {
            handleTempleFileUpload(targetTempleId, file);
          } else if (file && !targetTempleId) {
            showSnackbar('Could not determine selected temple for upload. Please try again.', 'error');
          }
          pendingTempleId.current = null;
          e.target.value = '';
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplesNearbyManager;
